/**
 * Modifier keys held on a hardware keyboard, tracked on window.
 *
 * Android Chrome (e.g. a Samsung tablet with an attached keyboard) can report
 * shiftKey/ctrlKey/metaKey = false on touch/pointer events from screen taps
 * even while Shift is held, so the event flags alone can't be trusted. We
 * listen on window (canvas focus doesn't matter) and reset on blur /
 * visibilitychange so a key released outside the page can't stick.
 */
export interface HeldModifiers {
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

export type ModifierFlags = { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean } | null | undefined;

export function createModifierTracker(target: Window = window) {
  const held: HeldModifiers = { shift: false, ctrl: false, meta: false };
  const set = (e: KeyboardEvent, down: boolean) => {
    if (e.key === 'Shift') held.shift = down;
    else if (e.key === 'Control') held.ctrl = down;
    else if (e.key === 'Meta' || e.key === 'OS') held.meta = down;
    // Keep in sync with the flags the keyboard event itself reports.
    if (!down || e.key !== 'Shift') held.shift = e.shiftKey || (down && e.key === 'Shift');
    if (!down || e.key !== 'Control') held.ctrl = e.ctrlKey || (down && e.key === 'Control');
    if (!down || (e.key !== 'Meta' && e.key !== 'OS')) held.meta = e.metaKey || (down && (e.key === 'Meta' || e.key === 'OS'));
  };
  const onDown = (e: KeyboardEvent) => set(e, true);
  const onUp = (e: KeyboardEvent) => set(e, false);
  const reset = () => {
    held.shift = false;
    held.ctrl = false;
    held.meta = false;
  };
  const onVis = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') reset();
  };
  target.addEventListener('keydown', onDown, true);
  target.addEventListener('keyup', onUp, true);
  target.addEventListener('blur', reset);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
  return {
    held,
    dispose() {
      target.removeEventListener('keydown', onDown, true);
      target.removeEventListener('keyup', onUp, true);
      target.removeEventListener('blur', reset);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    },
  };
}

/** Modifier held = the event's own flag OR the tracked keyboard state. */
export function isModifierHeld(evt: ModifierFlags, held: HeldModifiers | null | undefined): boolean {
  return !!(evt && (evt.shiftKey || evt.ctrlKey || evt.metaKey)) || !!(held && (held.shift || held.ctrl || held.meta));
}

export const LONG_PRESS_MS = 500;
